import type { Detection, ImageryThumb } from "./types";
import { lonLatToMercator, mercatorToLonLat } from "./geo";
import { FIRMS_HOME, WORLDVIEW_APP, latestProductTimeIso } from "./sources";

/** Must match worker/imagery.ts size=full GetMap pad so the overlay sits on the fire. */
export const MTG_GEO_FULL_PAD_M = 28000;
/** Must match worker/imagery.ts size=thumb GetMap pad. */
export const MTG_GEO_THUMB_PAD_M = 16000;

/** NASA GIBS / Worldview Snapshots layer ids keyed by our FIRMS source. */
const LAYERS: Record<string, { day: string; night: string; thermal: string }> = {
  VIIRS_SNPP: {
    day: "VIIRS_SNPP_CorrectedReflectance_TrueColor",
    night: "VIIRS_SNPP_DayNightBand_At_Sensor_Radiance",
    thermal: "VIIRS_SNPP_Thermal_Anomalies_375m_Day,VIIRS_SNPP_Thermal_Anomalies_375m_Night",
  },
  VIIRS_NOAA20: {
    day: "VIIRS_NOAA20_CorrectedReflectance_TrueColor",
    night: "VIIRS_NOAA20_DayNightBand_At_Sensor_Radiance",
    thermal: "VIIRS_NOAA20_Thermal_Anomalies_375m_Day,VIIRS_NOAA20_Thermal_Anomalies_375m_Night",
  },
  VIIRS_NOAA21: {
    day: "VIIRS_NOAA21_CorrectedReflectance_TrueColor",
    night: "VIIRS_NOAA21_DayNightBand_At_Sensor_Radiance",
    thermal: "VIIRS_NOAA21_Thermal_Anomalies_375m_Day,VIIRS_NOAA21_Thermal_Anomalies_375m_Night",
  },
  // MODIS has no day/night band — night still uses that UTC day's true color.
  MODIS: {
    day: "MODIS_Terra_CorrectedReflectance_TrueColor",
    night: "MODIS_Terra_CorrectedReflectance_TrueColor",
    thermal: "MODIS_Terra_Thermal_Anomalies_All",
  },
};

export function hasImagery(source: string): boolean {
  return source in LAYERS;
}

/** Daytime reflectance only — Worldview and stacked snapshots share this. */
export function snapshotTrueColor(source: string, satellite?: string): string | null {
  if (source === "MODIS") {
    const aqua = (satellite ?? "").toUpperCase().startsWith("A");
    return aqua ? "MODIS_Aqua_CorrectedReflectance_TrueColor" : "MODIS_Terra_CorrectedReflectance_TrueColor";
  }
  return LAYERS[source]?.day ?? null;
}

export function snapshotThermal(source: string, satellite?: string): string {
  if (source === "MODIS") {
    const aqua = (satellite ?? "").toUpperCase().startsWith("A");
    return aqua ? "MODIS_Aqua_Thermal_Anomalies_All" : "MODIS_Terra_Thermal_Anomalies_All";
  }
  return LAYERS[source]?.thermal ?? "VIIRS_SNPP_Thermal_Anomalies_375m_Day,VIIRS_SNPP_Thermal_Anomalies_375m_Night";
}

/**
 * What /api/imagery asks GIBS for. Always stack fire dots on a picture of the ground.
 * MODIS night used to be thermal-only — a black field with orange specks, while the
 * 256 px thumb often failed the empty-JPEG check and fell back to true color.
 */
export function snapshotLayer(source: string, night: boolean, satellite?: string): string | null {
  const thermal = snapshotThermal(source, satellite);
  if (source === "MODIS") {
    const color = snapshotTrueColor(source, satellite);
    return color ? `${color},${thermal}` : null;
  }
  const spec = LAYERS[source];
  if (!spec) return null;
  return `${night ? spec.night : spec.day},${thermal}`;
}

/** Worldview EPSG:4326 BBOX is south,west,north,east. */
export function snapshotBBox(
  lat: number,
  lon: number,
  padDeg: number,
): { south: number; west: number; north: number; east: number; wms: string } {
  const south = lat - padDeg;
  const north = lat + padDeg;
  const west = lon - padDeg;
  const east = lon + padDeg;
  return { south, west, north, east, wms: `${south},${west},${north},${east}` };
}

/** Daily GIBS composites are keyed by UTC date, not the exact overpass minute. */
export function snapshotDate(iso: string): string {
  return iso.slice(0, 10);
}

/** ~1 km grid so nearby pixels of the same daily composite share one cached JPEG. */
export function snapshotCoord(n: number): string {
  return n.toFixed(2);
}

/** Stable query string for /api/imagery — also the browser and Worker cache key. */
export function snapshotQuery(
  source: string,
  time: string,
  lat: number,
  lon: number,
  night: boolean,
  satellite?: string,
): URLSearchParams {
  const q = new URLSearchParams({
    source,
    time: snapshotDate(time),
    lat: snapshotCoord(lat),
    lon: snapshotCoord(lon),
    night: night ? "1" : "0",
    // Bust caches from the thermal-only MODIS night JPEGs.
    v: "2",
  });
  // Terra vs Aqua GIBS layers; omit for VIIRS so existing cache keys stay stable.
  if (source === "MODIS" && satellite) q.set("sat", satellite);
  return q;
}

/**
 * Collapse detections into unique satellite overpasses so the panel does not
 * show 40 identical thumbs for one fire. Prefer the last 24 h, then older.
 */
export function uniqueOverpasses(detections: Detection[], max = 6, nowMs = Date.now()): ImageryThumb[] {
  const groups = new Map<string, Detection>();
  for (const d of detections) {
    if (!hasImagery(d.source)) continue;
    const hour = d.observed_at.slice(0, 13);
    const key = `${d.source}|${d.satellite}|${hour}`;
    const prev = groups.get(key);
    if (!prev || (d.frp_mw ?? 0) > (prev.frp_mw ?? 0)) groups.set(key, d);
  }

  const dayAgo = nowMs - 24 * 3600 * 1000;
  const all = [...groups.values()].sort((a, b) => b.observed_at.localeCompare(a.observed_at));
  const recent = all.filter((d) => Date.parse(d.observed_at) >= dayAgo);
  const picked = (recent.length >= 2 ? recent : all).slice(0, max);

  return picked.map((d) => {
    const q = snapshotQuery(d.source, d.observed_at, d.lat, d.lon, d.daynight === "N", d.satellite);
    return {
      source: d.source,
      observed_at: d.observed_at,
      lat: d.lat,
      lon: d.lon,
      night: d.daynight === "N",
      thumb_url: `/api/imagery?${q.toString()}&size=thumb`,
      full_url: `/api/imagery?${q.toString()}&size=full`,
    };
  });
}

/** NW, NE, SE, SW in lon/lat — MapLibre image-source order. */
export function mtgGeoCoordinates(
  lat: number,
  lon: number,
  padM = MTG_GEO_FULL_PAD_M,
): [[number, number], [number, number], [number, number], [number, number]] {
  const [cx, cy] = lonLatToMercator(lon, lat);
  return [
    mercatorToLonLat(cx - padM, cy + padM),
    mercatorToLonLat(cx + padM, cy + padM),
    mercatorToLonLat(cx + padM, cy - padM),
    mercatorToLonLat(cx - padM, cy - padM),
  ];
}

/** One cached GeoColour JPEG around a fire — Play uses this instead of Europe XYZ tiles. */
export function mtgGeoFrameUrl(lat: number, lon: number, timeIso: string, size: "thumb" | "full" = "full"): string {
  const q = new URLSearchParams({
    source: "MTG",
    time: timeIso,
    lat: snapshotCoord(lat),
    lon: snapshotCoord(lon),
    size,
  });
  return `/api/imagery?${q.toString()}`;
}

/** Latest 10-minute MTG GeoColour frame around this fire — not a daily GIBS composite. */
export function mtgGeoThumb(lat: number, lon: number, nowMs = Date.now()): ImageryThumb {
  const observed_at = latestProductTimeIso(10, 20, nowMs);
  const q = new URLSearchParams({
    source: "MTG",
    time: observed_at,
    lat: snapshotCoord(lat),
    lon: snapshotCoord(lon),
  });
  return {
    source: "MTG",
    observed_at,
    lat,
    lon,
    night: false,
    thumb_url: `/api/imagery?${q.toString()}&size=thumb`,
    full_url: `/api/imagery?${q.toString()}&size=full`,
  };
}

/** FIRMS catalog for a sensor row. */
export function sourceCatalogUrl(_source: string): string {
  return FIRMS_HOME;
}

/** Open NASA Worldview at this overpass so the user can inspect the source granule. */
export function observationWorldviewUrl(d: Pick<Detection, "lat" | "lon" | "observed_at" | "source" | "daynight" | "satellite">): string {
  const pad = 0.45;
  const v = `${d.lon - pad},${d.lat - pad},${d.lon + pad},${d.lat + pad}`;
  const t = d.observed_at.replace(".000Z", "Z");
  // No GIBS Sentinel-3 FRP layer — place + time on NOAA-21 true colour (context, not the SLSTR granule).
  if (d.source === "S3") {
    return `${WORLDVIEW_APP}?v=${v}&t=${encodeURIComponent(t)}&l=VIIRS_NOAA21_CorrectedReflectance_TrueColor`;
  }
  const trueColor = snapshotTrueColor(d.source, d.satellite) ?? "VIIRS_SNPP_CorrectedReflectance_TrueColor";
  const thermal = snapshotThermal(d.source, d.satellite);
  const layers = d.daynight === "N" ? `${thermal},${trueColor}` : `${trueColor},${thermal}`;
  return `${WORLDVIEW_APP}?v=${v}&t=${encodeURIComponent(t)}&l=${layers}`;
}
